/**
 * Resolves the edge type between two agents from a topology definition.
 *
 * `routing.delivered` events carry the from/to agent ids but not the edge type;
 * the type comes from the topology's edges. Builds a lookup once (bidirectional
 * edges register both directions) and falls back to "custom" for unknown pairs.
 */

import type { EdgeType } from "../state/PixelWorldStore.js";
import type { TopologyInfo } from "../scenes/SceneLayout.js";

const KNOWN_EDGE_TYPES: readonly EdgeType[] = [
  "delegates-to",
  "reports-to",
  "peers-with",
  "pipes-to",
  "custom",
];

function normalizeEdgeType(type: string): EdgeType {
  return (KNOWN_EDGE_TYPES as readonly string[]).includes(type) ? (type as EdgeType) : "custom";
}

const SEP = " ";
const key = (from: string, to: string) => `${from}${SEP}${to}`;

export function createEdgeResolver(topo: TopologyInfo): (from: string, to: string) => EdgeType {
  const map = new Map<string, EdgeType>();
  for (const edge of topo.edges) {
    const type = normalizeEdgeType(edge.type);
    map.set(key(edge.from, edge.to), type);
    if (edge.bidirectional) map.set(key(edge.to, edge.from), type);
  }
  return (from, to) => map.get(key(from, to)) ?? "custom";
}
